<template>
	<Modal
		class="share-modal"
		:title="title"
		:force-backdrop-close="true"
		@close="close"
	>
		<!-- body -->
		<template #body>
			<div class="share-modal--body">
				<div
					v-if="!isMobileWebApp"
					class="share-modal--preview"
				>
					<div
						v-if="eventTitle"
						class="label"
					>
						{{ eventTitle }}
					</div>
					<div class="image">
						<div v-if="!imageLoadingError">
							<img
								:src="getScaledImage(imageUrl, 360)"
								alt=""
								@error="onImageError"
							>
						</div>
						<div
							v-if="imageLoadingError"
							class="preview-missing"
						>
							<div class="error-bg" />
							<span>{{ imageNotFoundLabel }}</span>
						</div>
					</div>
					<div class="share-modal--item-info">
						<slot name="item-title">
							<span class="item-title"> {{ itemLabel || item.name || item.title }} {{ goalScore }} </span>
							<span v-if="!isEvent">
								{{ duration }}
							</span>
						</slot>
					</div>
				</div>

				<!-- Share Url Input -->
				<div class="share-url">
					<FormField :label="urlLabel">
						<FormInput
							:value="urlBoxText"
							:classes="{ 'share-modal--url-copied': urlIsCopied }"
						>
							<template
								v-if="!urlIsCopied"
								#trailing
							>
								<a
									class="url-icon"
									@click="copyToClipboard"
								>
									<Icon
										icon-name="copy"
										size="lg"
									/>
								</a>
							</template>
						</FormInput>
					</FormField>
				</div>
			</div>
		</template>
		<!-- footer -->
		<template #footer-buttons>
			<!-- Social Media Buttons -->
			<FbShareButton
				:link="links.facebook"
				data-qa="share-on-fb"
			/>
			<TwitterShareButton
				:link="links.twitter"
				data-qa="share-on-twitter"
			/>
			<WhatsAppShareButton
				:link="links.whatsapp"
				data-qa="share-on-whatsapp"
			/>
			<MailShareButton
				:link="links.mail"
				data-qa="share-on-mail"
			/>
		</template>
	</Modal>
</template>

<script>
import copy from 'copy-to-clipboard';
import Icon from '@components/Icon';
import { mapGetters } from 'vuex';
import Modal from '../modal';
import { formatTimestampAsMinutesSeconds } from '@shared/utils/timeUtils';
import FormInput from '../../one/components/Form/FormInput';
import FormField from '../../one/components/Form/FormField';
import FbShareButton from '../social/FbShareButton';
import WhatsAppShareButton from '../social/WhatsAppShareButton';
import TwitterShareButton from '../social/TwitterShareButton';
import MailShareButton from '../social/MailShareButton';

export default {
	name:       'ShareModal',
	components: { MailShareButton, TwitterShareButton, FbShareButton, WhatsAppShareButton, FormField, FormInput, Modal, Icon },
	props:      {
		title: {
			type:    String,
			default: '',
		},
		body: {
			type:    String,
			default: '',
		},
		item: {
			type:     Object,
			required: true,
		},
		links: {
			// .facebook .twitter .mail .whatsapp
			type:     Object,
			required: true,
		},
		url: {
			type:    String,
			default: '',
		},
		imageNotFoundLabel: {
			type:    String,
			default: 'Preview not found',
		},
		urlLabel: {
			type:    String,
			default: 'Copy Link',
		},
		itemLabel: {
			type: String,
		},
		isEvent: {
			type:    Boolean,
			default: false,
		},
		eventTitle: {
			type:    String,
			default: '',
		},
		goalScore: {
			type:    String,
			default: '',
		},
	},
	data() {
		return {
			imageLoadingError: false,
			urlIsCopied:       false,
		};
	},
	computed: {
		...mapGetters({
			isMobileWebApp: 'app/isMobileWebApp',
		}),
		buttonClass() {
			return this.isMobileWebApp ? 'button--secondary' : 'button--primary';
		},
		duration() {
			return typeof this.item.duration === 'number' ? formatTimestampAsMinutesSeconds(this.item.duration) : this.item.duration;
		},
		imageUrl() {
			if (!this.imageLoadingError) {
				return this.item.imageUrl;
			}

			return null;
		},
		confirmationText() {
			return this.transUcfirst('commons.copied_message');
		},
		urlBoxText() {
			return this.urlIsCopied ? this.confirmationText : this.url;
		},
		urlBoxTextColorClass() {
			return this.urlIsCopied ? 'green-text' : '';
		},
		socialMediaLabel() {
			return this.$I18n ? this.$I18n.trans('one.social_media') : 'Social media';
		},
		cancelBtnLabel() {
			if (!this.$Helpers || !this.$I18n) {
				return '';
			}
			return this.$Helpers.ucfirst(this.$I18n.trans('commons.cancel'));
		},
	},
	methods: {
		close() {
			this.$emit('close');
		},
		copyToClipboard() {
			copy(this.url);
			this.urlIsCopied = true;
		},
		onImageError() {
			this.imageLoadingError = true;
		},
		getScaledImage(imageUrl) {
			return this.$Helpers ? this.$Helpers.getScaledImageUrl(imageUrl, 360) : imageUrl;
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';
@import '@videotools/sass/_fonts';

.share-modal--body {
	display: flex;
	flex-direction: column;

	.label {
		@include text-md-1;
		margin-bottom: 6px;
	}
}

.share-modal--preview {
	display: flex;
	flex-direction: column;
	margin-bottom: 20px;

	.label {
		@include text-md-2;
		margin-bottom: 8px;
	}

	.image {
		margin-bottom: 8px;

		img {
			height: 218px;
			width: 100%;
		}
	}

	.preview-missing {
		width: 100%;
		height: 218px;
		display: flex;
		flex-direction: column;

		.error-bg {
			height: 100%;
			background-position: center;
		}

		span {
			color: var(--color-grey-700);
		}

		text-align: center;
	}
}

.share-modal--item-info {
	display: flex;
	justify-content: space-between;

	.item-title {
		overflow: hidden;
		white-space: nowrap;
		text-overflow: ellipsis;
	}
}

.share-modal--url-copied::v-deep {
	color: var(--color-success);
	border: 1px solid var(--color-hype-yellow);
}

</style>
